import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useQuery, useMutation } from "@apollo/client";
import PersonIcon from "@mui/icons-material/Person";
import {
  GET_TASKS,
  UPDATE_TASK_OWNER_BULK,
} from "../../../apis/task-apis/taskApis";
import { TaskStatus } from "../../../utils/enums";
import TaskCard from "./TaskCard";
import TaskCardSkeleton from "../../../components/Skeletons/TaskCardSkeleton";
import UserSelectionDialog from "../../../components/UserSelectionDialog";

const TaskList = ({
  selectedTask,
  setSelectedTask,
  selectedDomain,
  selectedOwner,
  preselectedTaskId,
  refreshKey,
  onTaskOwnerChanged,
}) => {
  const theme = useTheme();
  const [statusFilter, setStatusFilter] = useState(TaskStatus.OPEN);
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());
  const [ownerDialogOpen, setOwnerDialogOpen] = useState(false);

  const { data: allData, refetch: refetchAllTasks } = useQuery(GET_TASKS, {
    variables: {
      filter: {},
      page: 1,
      limit: 1000,
    },
    skip: !preselectedTaskId,
  });

  const { data, loading, refetch: refetchTasks } = useQuery(GET_TASKS, {
    variables: {
      filter: {
        status_key: statusFilter,
      },
      page: 1,
      limit: 100,
    },
  });

  const { data: openData, refetch: refetchOpenTasks } = useQuery(GET_TASKS, {
    variables: {
      filter: {
        status_key: TaskStatus.OPEN,
      },
      page: 1,
      limit: 1000,
    },
  });

  const { data: resolvedData, refetch: refetchResolvedTasks } = useQuery(GET_TASKS, {
    variables: {
      filter: {
        status_key: TaskStatus.RESOLVED,
      },
      page: 1,
      limit: 1000,
    },
  });

  const tasks = useMemo(() => data?.getTasks?.data || [], [data]);
  const allTasks = useMemo(() => allData?.getTasks?.data || [], [allData]);
  const openTasks = openData?.getTasks?.data || [];
  const resolvedTasks = resolvedData?.getTasks?.data || [];

  const getFilteredCount = (taskList) => {
    if (selectedDomain) {
      return taskList.filter((task) =>
        task.domains?.some((domain) => domain.key === selectedDomain)
      ).length;
    }
    return taskList.length;
  };

  const openCount = getFilteredCount(openTasks);
  const resolvedCount = getFilteredCount(resolvedTasks);

  const filteredTasks = useMemo(
    () =>
      tasks
        .filter((task) => {
          if (selectedDomain) {
            return task.domains?.some((domain) => domain.key === selectedDomain);
          }
          return true;
        })
        .filter((task) => {
          if (selectedOwner === "__all__") return true;
          if (selectedOwner === "__unassigned__")
            return !task?.task_owner_data?.id;
          return task?.task_owner_data?.id === selectedOwner;
        })
        .sort((a, b) => {
          const priorityA = a.priority;
          const priorityB = b.priority;
          return priorityA - priorityB;
        }),
    [tasks, selectedDomain, selectedOwner]
  );

  useEffect(() => {
    if (!refreshKey) return;
    refetchTasks();
    refetchOpenTasks();
    refetchResolvedTasks();
    if (preselectedTaskId) {
      refetchAllTasks();
    }
  }, [
    refreshKey,
    preselectedTaskId,
    refetchAllTasks,
    refetchOpenTasks,
    refetchResolvedTasks,
    refetchTasks,
  ]);

  useEffect(() => {
    if (preselectedTaskId && allTasks.length > 0 && !selectedTask) {
      const taskIdToFind =
        typeof preselectedTaskId === "string"
          ? parseInt(preselectedTaskId, 10)
          : preselectedTaskId;
      const taskToSelect = allTasks.find((task) => task.id === taskIdToFind);

      if (taskToSelect) {
        if (
          taskToSelect.status_key &&
          taskToSelect.status_key !== statusFilter
        ) {
          setStatusFilter(taskToSelect.status_key);
        }
        setSelectedTask(taskToSelect);
        return;
      }
    }

    if (filteredTasks.length > 0) {
      const selectedTaskStillVisible = selectedTask
        ? filteredTasks.some((task) => task.id === selectedTask.id)
        : false;
      if (!selectedTask || !selectedTaskStillVisible) {
        setSelectedTask(filteredTasks[0]);
      }
    }
  }, [
    allTasks,
    filteredTasks,
    selectedTask,
    setSelectedTask,
    preselectedTaskId,
  ]);

  useEffect(() => {
    if (!selectedTask) return;
    const fresh = tasks.find((task) => task.id === selectedTask.id);
    if (fresh && fresh.task_owner_id !== selectedTask.task_owner_id) {
      setSelectedTask(fresh);
    }
  }, [tasks, selectedTask, setSelectedTask]);

  const visibleTaskIds = useMemo(
    () => filteredTasks.map((task) => task.id),
    [filteredTasks]
  );

  useEffect(() => {
    setSelectedTaskIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(visibleTaskIds);
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [visibleTaskIds]);

  const handleToggleTask = useCallback((taskId) => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      next.has(taskId) ? next.delete(taskId) : next.add(taskId);
      return next;
    });
  }, []);

 
  const handleSelectAll = useCallback(() => {
    setSelectedTaskIds((prev) =>
      prev.size === visibleTaskIds.length ? new Set() : new Set(visibleTaskIds)
    );
  }, [visibleTaskIds]);

  const clearSelection = useCallback(() => setSelectedTaskIds(new Set()), []);

  const [updateTaskOwner, { loading: ownerSaving }] = useMutation(
    UPDATE_TASK_OWNER_BULK
  );

  const handleBulkOwnerConfirm = useCallback(
    async (user) => {
      await updateTaskOwner({
        variables: {
          task_ids: Array.from(selectedTaskIds),
          task_owner_id: user?.id ?? null,
        },
      });
      setOwnerDialogOpen(false);
      clearSelection();
      onTaskOwnerChanged?.();
    },
    [updateTaskOwner, selectedTaskIds, clearSelection, onTaskOwnerChanged]
  );

  const selectedCount = selectedTaskIds.size;
  const allVisibleSelected =
    visibleTaskIds.length > 0 && selectedCount === visibleTaskIds.length;
  const someVisibleSelected =
    selectedCount > 0 && selectedCount < visibleTaskIds.length;

  return (
    <Box
      sx={{
        width: 350,
        flexShrink: 0,
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        border: `1px solid ${theme.customColors.border.main}`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          p: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          borderBottom: `1px solid ${theme.customColors.border.main}`,
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            backgroundColor: alpha(theme.customColors.border.main, 0.08),
            borderRadius: "8px",
            padding: "3px",
            gap: "4px",
            flexShrink: 0,
          }}
        >
          <Box
            onClick={() => setStatusFilter(TaskStatus.OPEN)}
            sx={{
              px: 2.5,
              py: 1,
              borderRadius: "6px",
              cursor: "pointer",
              backgroundColor:
                statusFilter === TaskStatus.OPEN ? "#FFFFFF" : "transparent",
              boxShadow:
                statusFilter === TaskStatus.OPEN
                  ? "0 1px 2px rgba(0,0,0,0.08)"
                  : "none",
              transition: "all 0.15s ease",
              "&:hover": {
                backgroundColor:
                  statusFilter === TaskStatus.OPEN
                    ? "#FFFFFF"
                    : alpha(theme.customColors.border.main, 0.05),
              },
            }}
          >
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: statusFilter === TaskStatus.OPEN ? 600 : 500,
                color:
                  statusFilter === TaskStatus.OPEN
                    ? theme.customColors.text.strong
                    : theme.customColors.text.secondary,
                transition: "all 0.15s",
              }}
            >
              Open ({openCount})
            </Typography>
          </Box>
          <Box
            onClick={() => setStatusFilter(TaskStatus.RESOLVED)}
            sx={{
              px: 2.5,
              py: 1,
              borderRadius: "6px",
              cursor: "pointer",
              backgroundColor:
                statusFilter === TaskStatus.RESOLVED
                  ? "#FFFFFF"
                  : "transparent",
              boxShadow:
                statusFilter === TaskStatus.RESOLVED
                  ? "0 1px 2px rgba(0,0,0,0.08)"
                  : "none",
              transition: "all 0.15s ease",
              "&:hover": {
                backgroundColor:
                  statusFilter === TaskStatus.RESOLVED
                    ? "#FFFFFF"
                    : alpha(theme.customColors.border.main, 0.05),
              },
            }}
          >
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: statusFilter === TaskStatus.RESOLVED ? 600 : 500,
                color:
                  statusFilter === TaskStatus.RESOLVED
                    ? theme.customColors.text.strong
                    : theme.customColors.text.secondary,
                transition: "all 0.15s",
              }}
            >
              Resolved ({resolvedCount})
            </Typography>
          </Box>
        </Box>

        {visibleTaskIds.length > 0 && (
          <Tooltip
            title={
              allVisibleSelected
                ? "Clear selection"
                : `Select all ${visibleTaskIds.length} tasks`
            }
            arrow
          >
            <Box
              onClick={handleSelectAll}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 0.75,
                py: 0.5,
                mr: 0.25,
                borderRadius: "6px",
                cursor: "pointer",
                flexShrink: 0,
                transition: "background-color 0.15s ease",
                "&:hover": {
                  backgroundColor: alpha(theme.customColors.border.main, 0.08),
                },
              }}
            >
              <Checkbox
                size="small"
                checked={allVisibleSelected}
                indeterminate={someVisibleSelected}
                onChange={handleSelectAll}
                onClick={(e) => e.stopPropagation()}
                inputProps={{ "aria-label": "Select all visible tasks" }}
                sx={{ p: 0 }}
              />
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  color: theme.customColors.text.secondary,
                }}
              >
                All
              </Typography>
            </Box>
          </Tooltip>
        )}
      </Box>

      <Collapse in={selectedCount > 0} timeout={150}>
        <Box
          sx={{
            px: 1.5,
            py: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderBottom: `1px solid ${theme.customColors.border.main}`,
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
          }}
        >
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              whiteSpace: "nowrap",
              color: theme.customColors.text.strong,
              flex: 1,
            }}
          >
            {selectedCount} selected
          </Typography>

          <Button
            variant="contained"
            size="small"
            startIcon={<PersonIcon sx={{ fontSize: 14 }} />}
            onClick={() => setOwnerDialogOpen(true)}
            sx={{
              textTransform: "none",
              borderRadius: "6px",
              fontSize: "12px",
              fontWeight: 500,
              py: 0.25,
              px: 1.25,
              minWidth: 0,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            Assign Owner
          </Button>
          <Button
            size="small"
            onClick={clearSelection}
            sx={{
              textTransform: "none",
              fontSize: "12px",
              fontWeight: 500,
              minWidth: 0,
              px: 0.75,
              color: theme.customColors.text.secondary,
            }}
          >
            Clear
          </Button>
        </Box>
      </Collapse>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 1.5,
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: alpha(theme.customColors.border.main, 0.7),
            borderRadius: "10px",
            "&:hover": {
              backgroundColor: alpha(theme.customColors.border.main, 0.7),
            },
          },
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {[1, 2, 3, 4, 5].map((index) => (
              <TaskCardSkeleton key={index} />
            ))}
          </Box>
        ) : filteredTasks.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 6,
              px: 4,
            }}
          >
            <Typography
              sx={{
                color: theme.customColors.text.strong,
                fontWeight: 600,
                mb: 1,
                fontSize: "18px",
              }}
            >
              No tasks found
            </Typography>
            <Typography
              sx={{
                color: theme.customColors.text.secondary,
                fontSize: "13px",
              }}
            >
              Try adjusting your filters to see more results.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {filteredTasks.map((task, index) => (
              <Box
                key={task.id}
                sx={{
                  borderBottom:
                    index < filteredTasks.length - 1
                      ? `1px solid ${theme.customColors.border.main}`
                      : "none",
                  pb: index < filteredTasks.length - 1 ? 1.25 : 0,
                  mb: index < filteredTasks.length - 1 ? 1.25 : 0,
                }}
              >
                <TaskCard
                  task={task}
                  isSelected={selectedTask?.id === task.id}
                  onClick={() => setSelectedTask(task)}
                  selectable
                  isChecked={selectedTaskIds.has(task.id)}
                  onToggleChecked={handleToggleTask}
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <UserSelectionDialog
        open={ownerDialogOpen}
        onClose={() => setOwnerDialogOpen(false)}
        title="Assign Task Owner"
        description={
          selectedCount === 1
            ? "1 task will be updated."
            : `${selectedCount} tasks will be updated.`
        }
        label="Task owner"
        currentUser={null}
        onConfirm={handleBulkOwnerConfirm}
        saving={ownerSaving}
        adminsOnly
        showUnassignOption
      />
    </Box>
  );
};

export default TaskList;
